/**
 * Event Tracking Utility for Student Hub
 * 
 * Features:
 * - Batched event sending (every 2-5 seconds or on route change)
 * - Debounced search tracking (500ms)
 * - IntersectionObserver for view tracking
 * - sendBeacon fallback for page unload
 * - Throttled view events (max 20/min per session)
 * - Session ID persistence in localStorage
 *
 * iter300 — Tracker uses `getApiBase()` so it picks same-origin on the
 * production custom domain (wassla.tech). The previous direct env-var
 * read called the preview backend cross-origin and blew up with CORS
 * errors, polluting the console.
 */

import { getApiBase } from '../api/httpClient';

const apiUrl = (path) => `${getApiBase()}${path}`;
const SESSION_STORAGE_KEY = 'wassla_session_id';
const BATCH_INTERVAL = 3000; // 3 seconds
const MAX_BATCH_SIZE = 30;
const VIEW_THROTTLE_LIMIT = 20; // Max views per minute
const SEARCH_DEBOUNCE = 500;

// =============================================================================
// SESSION MANAGEMENT
// =============================================================================

function getSessionId() {
  let sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }
  return sessionId;
}

// =============================================================================
// EVENT QUEUE
// =============================================================================

let eventQueue = [];
let batchTimer = null;
let viewsThisMinute = 0;
let viewThrottleReset = null;
let searchDebounceTimer = null;

// Reset view throttle every minute
function resetViewThrottle() {
  viewsThisMinute = 0;
  viewThrottleReset = setTimeout(resetViewThrottle, 60000);
}
resetViewThrottle();

/**
 * Add event to queue
 */
function queueEvent(event) {
  eventQueue.push({
    ...event,
    ts: Date.now(),
  });
  
  // Start batch timer if not running
  if (!batchTimer) {
    batchTimer = setTimeout(flushEvents, BATCH_INTERVAL);
  }
  
  // Flush if queue is full
  if (eventQueue.length >= MAX_BATCH_SIZE) {
    flushEvents();
  }
}

/**
 * Send queued events to server
 */
async function flushEvents() {
  if (batchTimer) {
    clearTimeout(batchTimer);
    batchTimer = null;
  }
  
  if (eventQueue.length === 0) return;
  
  const events = [...eventQueue];
  eventQueue = [];
  
  const payload = {
    sessionId: getSessionId(),
    userId: getUserId(), // May be null if not logged in
    events,
  };
  
  try {
    const response = await fetch(apiUrl(`/api/events/batch`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
      console.warn('[Tracker] Batch send failed:', response.status);
      // Re-queue events on failure (up to a limit)
      if (events.length < 50) {
        eventQueue = [...events, ...eventQueue].slice(0, MAX_BATCH_SIZE);
      }
    }
  } catch (error) {
    console.warn('[Tracker] Batch send error:', error);
    // Re-queue on network error
    if (events.length < 50) {
      eventQueue = [...events, ...eventQueue].slice(0, MAX_BATCH_SIZE);
    }
  }
}

/**
 * Use sendBeacon for page unload
 */
function flushBeacon() {
  if (eventQueue.length === 0) return;
  
  const payload = {
    sessionId: getSessionId(),
    userId: getUserId(),
    events: eventQueue,
  };
  
  // sendBeacon is fire-and-forget
  navigator.sendBeacon(
    apiUrl(`/api/events/batch`),
    JSON.stringify(payload)
  );
  
  eventQueue = [];
}

// Flush on page unload/hide
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushBeacon);
  window.addEventListener('pagehide', flushBeacon);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushBeacon();
    }
  });
}

/**
 * Get current user ID from auth state (if available)
 */
function getUserId() {
  try {
    // Try to get from localStorage auth token
    const token = localStorage.getItem('auth_token');
    if (token) {
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        return payload.user_id || payload.sub || null;
      }
    }
  } catch (e) {}
  return null;
}

// =============================================================================
// PUBLIC TRACKING FUNCTIONS
// =============================================================================

/**
 * Track item view (card visible in viewport)
 * Uses IntersectionObserver, call when item is 60% visible for 800ms
 */
export function trackView(itemId, category, meta = {}) {
  // Throttle views
  if (viewsThisMinute >= VIEW_THROTTLE_LIMIT) {
    return;
  }
  viewsThisMinute++;
  
  queueEvent({
    name: 'view',
    hub: 'student',
    category,
    itemId,
    meta,
  });
}

/**
 * Track item open (click to detail)
 */
export function trackOpen(itemId, category, meta = {}) {
  queueEvent({
    name: 'open',
    hub: 'student',
    category,
    itemId,
    meta,
  });
}

/**
 * Track save action
 */
export function trackSave(itemId, category, isSaved = true) {
  queueEvent({
    name: isSaved ? 'save' : 'unsave',
    hub: 'student',
    category,
    itemId,
  });
}

/**
 * Track apply/contact click (WhatsApp, Call, Apply button)
 */
export function trackApplyClick(itemId, category, ctaType = 'apply') {
  queueEvent({
    name: 'apply_click',
    hub: 'student',
    category,
    itemId,
    meta: { cta: ctaType },
  });
}

/**
 * Track share action
 */
export function trackShare(itemId, category, shareMethod = 'native') {
  queueEvent({
    name: 'share',
    hub: 'student',
    category,
    itemId,
    meta: { method: shareMethod },
  });
}

/**
 * Track search query (debounced 500ms)
 */
export function trackSearch(query, category = null, filters = {}) {
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }
  
  searchDebounceTimer = setTimeout(() => {
    queueEvent({
      name: 'search',
      hub: 'student',
      category,
      itemId: null,
      meta: { query, filters },
    });
  }, SEARCH_DEBOUNCE);
}

/**
 * Track filter change
 */
export function trackFilterChange(category, filters = {}) {
  queueEvent({
    name: 'filter_change',
    hub: 'student',
    category,
    itemId: null,
    meta: { filters },
  });
}

/**
 * Track new post created
 */
export function trackPostCreated(itemId, category, meta = {}) {
  queueEvent({
    name: 'post_created',
    hub: 'student',
    category,
    itemId,
    meta,
  });
}

/**
 * Force flush events (call on route change)
 */
export function flushTracking() {
  flushEvents();
}

// =============================================================================
// VIEW TRACKING HOOK (IntersectionObserver)
// =============================================================================

const viewedItems = new Set();
const VISIBILITY_THRESHOLD = 0.6; // 60% visible
const VISIBILITY_DURATION = 800; // 800ms

/**
 * Create an IntersectionObserver for view tracking
 * @param {string} category - The category of items being viewed
 * @returns {IntersectionObserver}
 */
export function createViewObserver(category) {
  const timers = new Map();
  
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const itemId = entry.target.dataset.itemId;
        if (!itemId) return;
        
        if (entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD) {
          // Start timer if not already started
          if (!timers.has(itemId)) {
            timers.set(
              itemId,
              setTimeout(() => {
                // Check if not already tracked
                if (!viewedItems.has(itemId)) {
                  viewedItems.add(itemId);
                  trackView(itemId, category);
                }
                timers.delete(itemId);
              }, VISIBILITY_DURATION)
            );
          }
        } else {
          // Cancel timer if item goes out of view
          if (timers.has(itemId)) {
            clearTimeout(timers.get(itemId));
            timers.delete(itemId);
          }
        }
      });
    },
    {
      threshold: VISIBILITY_THRESHOLD,
      rootMargin: '0px',
    }
  );
  
  return observer;
}

/**
 * Reset viewed items (call on category/level change)
 */
export function resetViewedItems() {
  viewedItems.clear();
}

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default {
  trackView,
  trackOpen,
  trackSave,
  trackApplyClick,
  trackShare,
  trackSearch,
  trackFilterChange,
  trackPostCreated,
  flushTracking,
  createViewObserver,
  resetViewedItems,
  getSessionId,
};
